import { jsxs, jsx } from 'react/jsx-runtime';
import { useReferenceSafeObject } from '@flex-design-system/fx-utils';
import { styled } from '@flex-design-system/stitches-react';
import { Primitive } from '@radix-ui/react-primitive';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { Tooltip } from '../../tooltip/Tooltip.mjs';
import { LoadingIndicator } from '../shared/components/loading-indicator/LoadingIndicator.mjs';
import { getButtonThemeColor } from '../utils/color/getButtonThemeColor.mjs';
import { getDisabledDataAttribute } from '../utils/data-attribute/getDisabledDataAttribute.mjs';
import { getGradientColorDataAttribute } from '../utils/data-attribute/getGradientColorDataAttribute.mjs';
import { iconButton, iconButtonClickable, iconButtonFocusVisible, iconButtonIconColor, iconButtonBackground, iconButtonContent } from './iconButton.vanilla.mjs';

const IconButtonImpl = forwardRef(
  ({
    children,
    className: classNameFromProps,
    color = "gray",
    danger,
    description,
    disabled,
    icon,
    loading,
    size,
    style: styleFromProps,
    tooltipSide,
    variant = "solid",
    ...props
  }, forwardedRef) => {
    const className = classNames(
      iconButton({ size, variant }),
      iconButtonClickable[variant],
      iconButtonFocusVisible,
      classNameFromProps
    );
    const style = useReferenceSafeObject(styleFromProps);
    const styles = useMemo(() => {
      const { background, color: iconColor } = getButtonThemeColor({
        color,
        danger,
        variant
      });
      return Object.assign(
        {},
        assignInlineVars({
          [iconButtonBackground]: background,
          [iconButtonIconColor]: iconColor
        }),
        style
      );
    }, [color, danger, style, variant]);
    const isDisabled = (disabled || loading) ?? false;
    return /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(Tooltip.Trigger, { asChild: true, children: /* @__PURE__ */ jsx(
        Primitive.button,
        {
          type: "button",
          "aria-disabled": isDisabled,
          "data-scope": "icon-button",
          "data-part": "root",
          className,
          disabled: isDisabled,
          ...getDisabledDataAttribute(isDisabled),
          ...getGradientColorDataAttribute(color),
          style: styles,
          ref: forwardedRef,
          ...props,
          children: /* @__PURE__ */ jsx(
            "div",
            {
              "data-scope": "icon-button",
              "data-part": "content",
              className: iconButtonContent,
              children: loading ? /* @__PURE__ */ jsx(
                LoadingIndicator,
                {
                  color,
                  danger,
                  size,
                  variant
                }
              ) : icon
            }
          )
        }
      ) }),
      description !== void 0 ? /* @__PURE__ */ jsx(Tooltip.Content, { side: tooltipSide, children: description }) : children
    ] });
  }
);
const IconButton = Object.assign({}, styled(IconButtonImpl), {
  TooltipContent: Tooltip.Content
});

export { IconButton };
//# sourceMappingURL=IconButton.mjs.map
